<script lang="ts">
    import {
        VpChevronLeftIcon,
        VpCloseIcon,
    } from '@decathlon-experience/icons/svelte';
    import { createDialog } from '@melt-ui/svelte';
    import dayjs from 'dayjs';
    import { fade } from 'svelte/transition';

    import { trans } from '$assets/shop-translator.js';
    import resetOnBfCache from '$common/utils/resetOnBfCache';
    import {
        trackAddToBooking,
        trackBookingStarted,
        trackDateSelected,
        trackPaxSelectedDebounced,
        type BookingAnalyticsPayload,
    } from '$saga/analytics';
    import { TOUR_TYPE_FIT, TOUR_TYPE_GIR } from '$saga/constants/tour';
    import { flyDrawer } from '$saga/utils/customTransitions';
    import { formatCurrency } from '$saga/utils/price';
    import SelectPaxStepForm from '$saga/views/Tour/Aside/form/SelectPaxStepForm/SelectPaxStepForm.svelte';
    import SelectRoomPaxStepForm from '$saga/views/Tour/Aside/form/SelectPaxStepForm/SelectRoomPaxStepForm.svelte';
    import {
        alignAgesWithCount,
        resolveAdultMaxAge,
        shouldShowIndividualAges,
    } from '$saga/views/Tour/Aside/form/SelectPaxStepForm/utils';
    import SelectSnowtrexDateStepForm from '$saga/views/Tour/Aside/form/SelectSnowtrexDateStepForm.svelte';
    import SelectTourDateStepForm from '$saga/views/Tour/Aside/form/SelectTourDateStepForm.svelte';
    import PriceAside from '$saga/views/Tour/Aside/PriceAside.svelte';
    import UnavailableTour from '$saga/views/Tour/Aside/UnavailableTour.svelte';
    import {
        datesInfos,
        isModalOpen,
        modalStep,
        resetSelectedPrice,
        selectedPrice,
        snowtrexPendingSelection,
        snowtrexSelectedDuration,
        snowtrexSelectedPax,
    } from '$saga/views/Tour/store';
    import type {
        AgeRules,
        FormValues,
        TourConfig,
    } from '$saga/views/Tour/types';
    import { filterTourDatesIsTravelAloneAuthorized } from '$saga/views/Tour/utils';
    import { getSnowtrexAccommodationPrice } from '$saga/views/Tour/VisibleDates/SnowtrexVisibleDates/utils';
    import type {
        MonthlyPrice,
        SnowtrexSelection,
    } from '$saga/views/Tour/VisibleDates/types';

    export let tourConfig: TourConfig;
    export let tourBookingAnalytics: Readonly<BookingAnalyticsPayload> | null =
        null;
    export let hideStock: boolean = false;

    /** constants **/
    const isGir = tourConfig.tourType === TOUR_TYPE_GIR;
    const isFit = tourConfig.tourType === TOUR_TYPE_FIT;
    const fitMonthlyPrices = Object.values(
        tourConfig.cheapestMonthlyPrices,
    ).filter(
        (price): price is MonthlyPrice =>
            undefined !== price &&
            null !== price &&
            'amount' in price &&
            'number' === typeof price.amount,
    );
    const cheapestFitMonthlyPrice =
        fitMonthlyPrices.reduce<MonthlyPrice | null>(
            (cheapest, price) =>
                null === cheapest || price.amount < cheapest.amount
                    ? price
                    : cheapest,
            null,
        );
    const minPrice =
        isFit && null !== cheapestFitMonthlyPrice
            ? cheapestFitMonthlyPrice.amount / 100
            : tourConfig.minPrice;
    const originalPrice =
        isFit && null !== cheapestFitMonthlyPrice
            ? 'number' === typeof cheapestFitMonthlyPrice.originalAmount
                ? cheapestFitMonthlyPrice.originalAmount / 100
                : null
            : tourConfig.originalPrice;
    const rebateAmount =
        isFit && null !== cheapestFitMonthlyPrice
            ? 'number' === typeof cheapestFitMonthlyPrice.rebateAmount
                ? cheapestFitMonthlyPrice.rebateAmount / 100
                : null
            : tourConfig.rebateAmount;
    const ageRules: AgeRules = {
        max: {
            isUnder18Allowed:
                tourConfig.maxAgeAllowed != null &&
                tourConfig.maxAgeAllowed < 18,
        },
        min: {
            isUnder18Allowed:
                tourConfig.minAgeAllowed != null &&
                tourConfig.minAgeAllowed < 18,
        },
    };
    const isQuote = !tourConfig.isBookable || !tourConfig.isEbooking;
    const showIndividualAges = shouldShowIndividualAges({
        isQuote,
        hasTravelGateHotels: tourConfig.hasTravelGateHotels,
        hasMultipleAgePricing: tourConfig.hasMultipleAgePricing,
    });
    const showAdultAges =
        showIndividualAges &&
        (tourConfig.hasAdultAgePricing || tourConfig.hasTravelGateHotels);
    const showChildAges =
        showIndividualAges &&
        (tourConfig.hasChildAgePricing || tourConfig.hasTravelGateHotels);
    const adultMinAge =
        tourConfig.adultMinAge ?? Math.max(18, tourConfig.minAgeAllowed ?? 0);
    const adultMaxAge = resolveAdultMaxAge(
        tourConfig.adultMaxAge,
        tourConfig.maxAgeAllowed,
        tourConfig.maxAgeAllowedForValidation,
    );
    const childMinAge =
        tourConfig.minAgeAllowed !== null && tourConfig.minAgeAllowed < 18
            ? Math.max(0, tourConfig.minAgeAllowed)
            : 0;
    const childMaxAge = Math.min(17, tourConfig.maxAgeAllowed ?? 17);
    const initialAdults = ageRules.max.isUnder18Allowed
        ? 0
        : tourConfig.isTravelAloneAuthorized
          ? 1
          : 2;
    const createInitialFormState = (): FormValues => ({
        selectedDate: null,
        adults: initialAdults,
        children: 0,
        childrenAges: [],
        adultsAges: Array.from({ length: initialAdults }, () => ''),
    });

    const availableDates = filterTourDatesIsTravelAloneAuthorized(
        tourConfig.visibleDates,
        tourConfig.isTravelAloneAuthorized,
    );

    /** States **/

    let formValues: FormValues = createInitialFormState();
    let sectionRef: HTMLFormElement;
    let accommodationId: string | null = null;
    let circuitDateId: string | null = null;
    let snowtrexSelectionKey: string | null = null;
    let lastTrackedPax = {
        adults: formValues.adults,
        children: formValues.children,
    };
    let isLoading = false;
    const isBookingClosed =
        tourConfig.isClosedSales ||
        (isGir && (!tourConfig.isBookable || availableDates.length === 0));

    /** DERIVED STATES **/
    // reactive effect on datesInfos in order to update selectedDate in formValues
    // when user select another date from the VisibleDates component
    $: {
        if (!tourConfig.isSnowtrex && $datesInfos) {
            formValues.selectedDate = $datesInfos.selectedDate;
        }
    }

    const getSnowtrexSelectionKey = (selection: SnowtrexSelection): string =>
        `${selection.circuitDateId}:${selection.departureDate}:${selection.returnDate}:${selection.duration}:${selection.accommodationId}:${selection.pax}`;

    $: if (tourConfig.isSnowtrex && $snowtrexPendingSelection) {
        const selection = $snowtrexPendingSelection;
        const selectionKey = getSnowtrexSelectionKey(selection);
        if (selectionKey !== snowtrexSelectionKey) {
            formValues = {
                ...formValues,
                adults: selection.adults,
                children: selection.children,
                adultsAges: alignAgesWithCount([], selection.adults),
                childrenAges: alignAgesWithCount([], selection.children),
                selectedDate: selection.departureDate,
            };
            accommodationId = selection.accommodationId;
            circuitDateId = selection.circuitDateId;
            snowtrexSelectionKey = selectionKey;
        }
    }

    $: if (tourConfig.isSnowtrex && $snowtrexPendingSelection) {
        const selection = $snowtrexPendingSelection;
        const pax = formValues.adults + formValues.children;
        const price = getSnowtrexAccommodationPrice(
            tourConfig.snowtrexAccommodationDates,
            selection,
            pax,
        );

        if (
            price !== null &&
            (selection.pax !== pax ||
                selection.adults !== formValues.adults ||
                selection.children !== formValues.children)
        ) {
            const updatedSelection = {
                ...selection,
                pax,
                adults: formValues.adults,
                children: formValues.children,
                selectedPrice: price,
            };
            snowtrexPendingSelection.set(updatedSelection);
            snowtrexSelectedPax.set(pax);
            snowtrexSelectionKey = getSnowtrexSelectionKey(updatedSelection);
            $selectedPrice = {
                originalPrice: price.originalPrice,
                actualPrice: price.price,
                rebateAmount: price.rebateAmount,
            };
        }
    }

    /** Analytics **/
    // Track pax changes with debounce to avoid event spam
    $: {
        const paxChanged =
            formValues.adults !== lastTrackedPax.adults ||
            formValues.children !== lastTrackedPax.children;

        // Only track when on pax step AND we have a valid date selected
        if (
            paxChanged &&
            tourBookingAnalytics &&
            $modalStep === 2 &&
            formValues.selectedDate
        ) {
            // Track pax selection: Tour + Date + Price + Pax
            trackPaxSelectedDebounced(tourBookingAnalytics, {
                start_date: formValues.selectedDate,
                selected_price: $selectedPrice?.actualPrice ?? null,
                adults: formValues.adults,
                children: formValues.children,
            });
            lastTrackedPax = {
                adults: formValues.adults,
                children: formValues.children,
            };
        }
    }

    const resetForm = () => {
        isLoading = false;
        formValues = createInitialFormState();
        lastTrackedPax = {
            adults: formValues.adults,
            children: formValues.children,
        };
        if (tourConfig.isSnowtrex) {
            accommodationId = null;
            circuitDateId = null;
            snowtrexSelectionKey = null;
            snowtrexPendingSelection.set(null);
            snowtrexSelectedDuration.set(null);
            resetSelectedPrice();
        }
        $modalStep = 1;
    };

    // Melt UI Dialog
    const {
        elements: { trigger, title, overlay, content, portalled },
        states: { open },
    } = createDialog({
        open: isModalOpen,
        forceVisible: true,
        onOpenChange: ({ curr, next }) => {
            if (curr && !next) {
                resetForm();
            }

            return next;
        },
    });

    // Track booking_started when modal opens: Tour only (context)
    $: if ($open && tourBookingAnalytics) {
        trackBookingStarted(tourBookingAnalytics);
    }

    /** Actions **/
    const handleNextStep = (date: string, price?: number) => {
        // reset
        formValues = {
            ...createInitialFormState(),
            selectedDate: date,
        };
        isLoading = false;

        $modalStep = 2;

        // Track date selection from SelectTourDateStepForm: Tour + Date + Price
        if (tourBookingAnalytics) {
            trackDateSelected(tourBookingAnalytics, {
                start_date: date,
                selected_price: price ?? null,
            });
        }
    };

    const handleSnowtrexAccommodationSelected = (
        selection: SnowtrexSelection,
    ) => {
        const hasMatchingPax =
            formValues.adults + formValues.children === selection.pax;
        const selectionWithComposition = {
            ...selection,
            adults: hasMatchingPax ? formValues.adults : selection.pax,
            children: hasMatchingPax ? formValues.children : 0,
        };
        snowtrexSelectedPax.set(selectionWithComposition.pax);
        snowtrexPendingSelection.set(selectionWithComposition);
        $modalStep = 2;
    };

    // Redirects to ebooking funnel or request funnel
    const handleSubmit = (event: SubmitEvent) => {
        isLoading = true;

        const form = event.target as HTMLFormElement;
        form.action =
            tourConfig.isBookable && tourConfig.isEbooking
                ? tourConfig.bookingFormAction
                : tourConfig.bookingRequestAction;
        form.method =
            tourConfig.isBookable && tourConfig.isEbooking ? 'POST' : 'GET';

        if (tourConfig.isBookable) {
            window.triggerAddToCart(true);
        }

        // Track add_to_booking: Tour + Date + Price + Pax (complete booking data)
        if (tourBookingAnalytics && formValues.selectedDate) {
            trackAddToBooking(tourBookingAnalytics, {
                start_date: formValues.selectedDate,
                selected_price: $selectedPrice?.actualPrice ?? null,
                adults: formValues.adults,
                children: formValues.children,
            });
        }

        form.submit();
    };
</script>

{#if isBookingClosed}
    <UnavailableTour
        allTourUrl={tourConfig.allTourUrl}
        {originalPrice}
        {rebateAmount}
        {minPrice}
    />
{:else}
    <div
        class="fixed bottom-0 left-0 flex gap-xs w-full items-center justify-between border-t border-solid border-border-quiet
    bg-container-commercial-neutral px-m py-s text-content-neutral desktop:sticky
    desktop:right-0 desktop:top-[100px] desktop:block desktop:w-[300px] desktop:min-w-[300px] desktop:flex-col desktop:border-none"
    >
        {#if !isBookingClosed}
            <PriceAside
                {originalPrice}
                {rebateAmount}
                {minPrice}
                isSnowtrex={tourConfig.isSnowtrex}
                snowtrexAccommodationDates={tourConfig.snowtrexAccommodationDates}
                selectedDuration={$snowtrexSelectedDuration}
            />
        {/if}

        <div class="w-full desktop:mt-m">
            <button
                class="vp-button vp-button--squared vp-button--primary vp-button--small w-full whitespace-nowrap desktop:px-xl desktop:py-s desktop:text-sm"
                {...$trigger}
                use:trigger
            >
                {#if tourConfig.isBookable && tourConfig.isEbooking}
                    {trans('common.book')}
                {:else}
                    {trans('tour.aside_panel.action.request')}
                {/if}
            </button>
        </div>
    </div>
{/if}

{#if $open}
    <section {...$portalled} use:portalled use:resetOnBfCache={resetForm}>
        <div
            {...$overlay}
            use:overlay
            class="fixed inset-0 z-40 bg-black/50"
            transition:fade={{ duration: 250 }}
        ></div>
        <section
            {...$content}
            use:content
            class="fixed inset-x-0 bottom-0 z-50 flex h-full max-h-[calc(100%)] w-full flex-col bg-white transition-all duration-500 desktop:inset-x-[unset] desktop:right-0 desktop:top-0 desktop:min-w-[33vw] desktop:max-w-[33vw]"
            transition:flyDrawer
        >
            <section class="flex h-full flex-col overflow-hidden">
                <!-- Header -->
                <div
                    class="sticky top-0 z-50 mx-m flex min-h-[65px] items-center justify-center border-b border-solid border-border-quiet bg-white py-m desktop:mx-3xl"
                >
                    {#if $modalStep === 2}
                        <button
                            on:click={() => {
                                if (tourConfig.isSnowtrex) {
                                    snowtrexSelectedPax.set(
                                        formValues.adults + formValues.children,
                                    );
                                }
                                $modalStep = 1;
                            }}
                        >
                            <VpChevronLeftIcon
                                class="size-xl text-content-neutral"
                            />
                        </button>
                    {/if}
                    <div class="mx-auto" {...$title} use:title>
                        {#if $modalStep === 1}
                            <div class="font-semibold">
                                {trans('tour.aside_panel.departure_date')}
                            </div>
                        {:else if $modalStep === 2}
                            <div class="flex flex-col gap-2xs">
                                <div class="text-center">
                                    <span class="font-semibold"
                                        >{trans(
                                            'tour.aside_panel.action.min_price',
                                        )}</span
                                    >
                                    <span class="text-xl font-semibold">
                                        {#if isGir && !tourConfig.isSnowtrex}
                                            {`${formatCurrency($datesInfos.price ?? 0, false, 'EUR', true)}`}
                                        {:else if isFit || tourConfig.isSnowtrex}
                                            {`${formatCurrency($selectedPrice?.actualPrice ?? minPrice ?? 0, false, 'EUR', true)}`}
                                        {/if}
                                    </span>
                                    <span class="text-sm"
                                        >{trans(
                                            'tour.aside_panel.action.by_pers',
                                        )}</span
                                    >
                                </div>
                                <div
                                    class="text-center text-sm text-content-quiet"
                                >
                                    {trans(
                                        'tour.aside_panel.dates.departure_from',
                                    )}
                                    {dayjs(
                                        formValues.selectedDate,
                                        'YYYY-MM-DD',
                                    ).format('DD MMM YYYY')}
                                    {trans(
                                        'tour.aside_panel.dates.departure_to',
                                    )}
                                    {#if tourConfig.isSnowtrex && $snowtrexPendingSelection}
                                        {dayjs(
                                            $snowtrexPendingSelection.returnDate,
                                            'YYYY-MM-DD',
                                        ).format('DD MMM YYYY')}
                                    {:else}
                                        {dayjs(
                                            formValues.selectedDate,
                                            'YYYY-MM-DD',
                                        )
                                            .add(
                                                tourConfig.tourDuration - 1,
                                                'day',
                                            )
                                            .format('DD MMM YYYY')}
                                    {/if}
                                </div>
                            </div>
                        {/if}
                    </div>
                    {#if $modalStep === 1}
                        <button
                            on:click={() => {
                                $open = false;
                            }}
                            aria-label={trans('action.close')}
                        >
                            <VpCloseIcon class="size-xl text-content-neutral" />
                        </button>
                    {/if}
                </div>

                <!-- Content-->
                <form
                    bind:this={sectionRef}
                    id="prefunnel"
                    on:submit|preventDefault|stopPropagation={handleSubmit}
                    class="no-scrollbar flex flex-1 flex-col overflow-y-auto px-l desktop:px-3xl"
                >
                    <input
                        type="hidden"
                        name="startDate"
                        bind:value={formValues.selectedDate}
                    />
                    {#if tourConfig.isSnowtrex}
                        <input
                            type="hidden"
                            name="endDate"
                            value={$snowtrexPendingSelection?.returnDate ?? ''}
                        />
                    {/if}
                    <input
                        class="hidden"
                        name="token"
                        bind:value={tourConfig.token}
                    />
                    <input
                        class="hidden"
                        name="code"
                        bind:value={tourConfig.code}
                    />
                    <input
                        class="hidden"
                        name="csrf_token"
                        bind:value={tourConfig.csrfToken}
                    />
                    {#if tourConfig.isSnowtrex}
                        <input
                            type="hidden"
                            name="circuitDateId"
                            bind:value={circuitDateId}
                        />
                        <input
                            type="hidden"
                            name="roomCriterionId"
                            bind:value={accommodationId}
                        />
                    {/if}

                    {#if $modalStep === 1}
                        {#if tourConfig.isSnowtrex}
                            <SelectSnowtrexDateStepForm
                                snowtrexAccommodationDates={tourConfig.snowtrexAccommodationDates}
                                cheapestMonthlyPrices={tourConfig.cheapestMonthlyPrices}
                                onAccommodationSelected={handleSnowtrexAccommodationSelected}
                            />
                        {:else}
                            <SelectTourDateStepForm
                                tourType={tourConfig.tourType}
                                dates={availableDates}
                                closedSales={tourConfig.closedSales}
                                minPricePerDays={tourConfig.minPricePerDays}
                                {handleNextStep}
                                overflowContainer={sectionRef}
                                {hideStock}
                            />
                        {/if}
                    {:else if $modalStep === 2}
                        {#if tourConfig.hasTravelGateHotels}
                            <SelectRoomPaxStepForm
                                tourType={tourConfig.tourType}
                                bookingType={tourConfig.bookingType}
                                isTravelAloneAuthorized={tourConfig.isTravelAloneAuthorized}
                                minAgeAllowed={tourConfig.minAgeAllowed}
                                {showIndividualAges}
                                {showAdultAges}
                                {showChildAges}
                                {adultMinAge}
                                {adultMaxAge}
                                {childMinAge}
                                {childMaxAge}
                                maxAgeAllowed={tourConfig.maxAgeAllowed}
                                requiredPax={$snowtrexPendingSelection?.pax ??
                                    null}
                                bind:formValues
                                {ageRules}
                                {isLoading}
                            />
                        {:else}
                            <SelectPaxStepForm
                                tourType={tourConfig.tourType}
                                bookingType={tourConfig.bookingType}
                                isTravelAloneAuthorized={tourConfig.isTravelAloneAuthorized}
                                minAgeAllowed={tourConfig.minAgeAllowed}
                                {showIndividualAges}
                                {showAdultAges}
                                {showChildAges}
                                {adultMinAge}
                                {adultMaxAge}
                                {childMinAge}
                                {childMaxAge}
                                maxAgeAllowed={tourConfig.maxAgeAllowed}
                                requiredPax={$snowtrexPendingSelection?.pax ??
                                    null}
                                {ageRules}
                                bind:formValues
                                {hideStock}
                                {isLoading}
                            />
                        {/if}
                    {/if}
                </form>
            </section>
        </section>
    </section>
{/if}
